import { supabase } from '@/integrations/supabase/client';

export interface MinorContactCheck {
  canContact: boolean;
  isMinor: boolean;
  requiresParentContact: boolean;
  message: string;
  parentId?: string;
}

// States with different age of majority laws for NIL
const STATE_AGE_RULES: Record<string, number> = {
  'Nebraska': 19, // Nebraska considers 18 as minor for NIL purposes
  'NE': 19,
};

const DEFAULT_MINOR_AGE = 18;

/**
 * Determines if a user is considered a minor based on their age and state
 */
export const isUserMinorForNIL = (age: number | undefined, stateProvince: string | undefined): boolean => {
  if (!age) return true; // Default to minor if age unknown (safest)
  
  const minorAge = stateProvince ? (STATE_AGE_RULES[stateProvince] || DEFAULT_MINOR_AGE) : DEFAULT_MINOR_AGE;
  return age < minorAge;
};

/**
 * Gets the age threshold for a specific state
 */
export const getMinorAgeThreshold = (stateProvince: string | undefined): number => {
  return stateProvince ? (STATE_AGE_RULES[stateProvince] || DEFAULT_MINOR_AGE) : DEFAULT_MINOR_AGE;
};

/**
 * Checks if an agent can contact an athlete
 * Returns detailed info about contact permissions
 */
export const checkAgentCanContactAthlete = async (
  athleteId: string,
  athleteUserType: 'internal' | 'supabase'
): Promise<MinorContactCheck> => {
  try {
    let age: number | undefined;
    let stateProvince: string | undefined;
    let parentId: string | undefined;

    if (athleteUserType === 'internal') {
      // Fetch from internal_user_profiles
      const { data: profile, error } = await supabase
        .from('internal_user_profiles')
        .select('age, state_province, user_id')
        .eq('user_id', athleteId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching internal profile:', error);
        // Default to most restrictive if we can't fetch
        return {
          canContact: false,
          isMinor: true,
          requiresParentContact: true,
          message: 'Unable to verify athlete age. Please contact support.'
        };
      }

      age = profile?.age || undefined;
      stateProvince = profile?.state_province || undefined;

      // Also check internal_users for age_group as fallback
      if (!age) {
        const { data: internalUser } = await supabase
          .from('internal_users')
          .select('age_group')
          .eq('id', athleteId)
          .maybeSingle();

        if (internalUser?.age_group) {
          if (internalUser.age_group === '13-and-under' || 
              internalUser.age_group === '14-17' ||
              parseInt(internalUser.age_group) <= 17) {
            age = 15; // Treat as minor
          } else if (internalUser.age_group === '18-plus') {
            age = 18;
          }
        }
      }

    } else {
      // Fetch from profiles (supabase users)
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('age_group')
        .eq('id', athleteId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return {
          canContact: false,
          isMinor: true,
          requiresParentContact: true,
          message: 'Unable to verify athlete age. Please contact support.'
        };
      }

      // Parse age from age_group
      if (profile?.age_group) {
        if (profile.age_group === '13-and-under') {
          age = 12;
        } else if (profile.age_group === '14-17') {
          age = 15;
        } else if (profile.age_group === '18-plus') {
          age = 18;
        }
      }
    }

    const isMinor = isUserMinorForNIL(age, stateProvince);
    const threshold = getMinorAgeThreshold(stateProvince);

    if (isMinor) {
      const stateNote = stateProvince && STATE_AGE_RULES[stateProvince] 
        ? ` (${stateProvince} requires age ${STATE_AGE_RULES[stateProvince]}+)`
        : '';
      
      return {
        canContact: false,
        isMinor: true,
        requiresParentContact: true,
        parentId,
        message: `Unable to contact minors directly. Please reach out to the parent of this Player${stateNote}.`
      };
    }

    return {
      canContact: true,
      isMinor: false,
      requiresParentContact: false,
      message: 'Contact allowed'
    };
  } catch (error) {
    console.error('Error checking contact permissions:', error);
    return {
      canContact: false,
      isMinor: true,
      requiresParentContact: true,
      message: 'Unable to verify contact permissions. Please try again.'
    };
  }
};

/**
 * Gets the minor communication rules message for registration/dashboard
 */
export const getMinorCommunicationRulesMessage = (): string[] => {
  return [
    'Your parent must have a "Players Lockeroom Parent" account type.',
    'They must be connected to you. If you have not provided them with your Connect ID, please provide it so the Parent-to-Minor connection can be made.',
    'Communication from agents/NIL representatives will go to your Parent first. The Parent can add you to the messaging if they wish, but for safety on the Players Lockeroom Platform, we will have the Adult acknowledged.',
    'You will be notified when someone shows interest. Notification will go to the parent, and you will receive a "Someone looks interested, reach out to your Parent(s) for more information" notification.'
  ];
};

/**
 * Records an agent's attempt to contact a minor
 * This creates a notification for both parent and child
 */
export const recordAgentInterestInMinor = async (
  agentUserId: string,
  agentUserType: 'internal' | 'supabase',
  athleteId: string,
  athleteUserType: 'internal' | 'supabase'
): Promise<{ success: boolean; error?: string }> => {
  try {
    const monthYear = new Date().toISOString().slice(0, 7);

    // Record the interest attempt
    await supabase
      .from('agent_athlete_contacts')
      .insert({
        agent_user_id: agentUserId,
        agent_user_type: agentUserType,
        athlete_user_id: athleteId,
        athlete_user_type: athleteUserType,
        contact_type: 'interest_blocked_minor',
        month_year: monthYear
      });

    // Create admin notification for tracking
    await supabase
      .from('admin_notifications')
      .insert({
        notification_type: 'agent_minor_contact_attempt',
        title: 'Agent Minor Contact Attempt',
        message: `Agent ${agentUserId} attempted to contact minor ${athleteId}. Contact was blocked per platform policy.`,
        priority: 'low'
      });

    return { success: true };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('Error recording agent interest:', error);
    return { success: false, error: errorMessage };
  }
};

export const minorContactProtectionService = {
  isUserMinorForNIL,
  getMinorAgeThreshold,
  checkAgentCanContactAthlete,
  getMinorCommunicationRulesMessage,
  recordAgentInterestInMinor
};
